import type { OneSignalConfig } from '@erp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button, Card, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { getPushConfig, sendPushTest, updatePushConfig } from '../../lib/push.js';

export function PushNotificationsPage(): JSX.Element {
  const { t } = useTranslation('push');
  const { t: tc } = useTranslation();
  const [config, setConfig] = useState<OneSignalConfig | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [appId, setAppId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [segment, setSegment] = useState('Subscribed Users');
  const [savingCfg, setSavingCfg] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const c = await getPushConfig();
      setConfig(c); setEnabled(c.enabled); setAppId(c.appId ?? ''); setSegment(c.defaultSegment);
    } catch { toast.error(tc('errors:server')); }
  };
  useEffect(() => { void load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const saveConfig = async (): Promise<void> => {
    setSavingCfg(true);
    try {
      const c = await updatePushConfig({
        enabled, defaultSegment: segment.trim() || 'Subscribed Users',
        appId: appId.trim() || null,
        ...(apiKey.trim() ? { restApiKey: apiKey.trim() } : {}),
      });
      setConfig(c); setEnabled(c.enabled); setAppId(c.appId ?? ''); setSegment(c.defaultSegment); setApiKey('');
      toast.success(tc('actions.saved', { defaultValue: 'Saved' }));
    } catch { toast.error(tc('errors:server')); } finally { setSavingCfg(false); }
  };

  const clearKey = async (): Promise<void> => {
    setSavingCfg(true);
    try { const c = await updatePushConfig({ restApiKey: null }); setConfig(c); setApiKey(''); toast.success(t('config.keyCleared', { defaultValue: 'API key removed' })); }
    catch { toast.error(tc('errors:server')); } finally { setSavingCfg(false); }
  };

  const runTest = async (): Promise<void> => {
    setTesting(true);
    try {
      const res = await sendPushTest();
      if (res.ok) toast.success(t('result.sent', { defaultValue: 'Sent to {{n}} device(s)', n: res.recipients ?? 0 }));
      else toast.error(t('result.failed', { defaultValue: 'Failed: {{e}}', e: res.error ?? '' }));
    } catch (e) {
      const code = (e as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      toast.error(code === 'not_configured' ? t('needConfig', { defaultValue: 'Set your OneSignal App ID and API key first.' }) : tc('errors:server'));
    } finally { setTesting(false); }
  };

  if (!config) {
    return <Card><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;
  }


  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('title', { defaultValue: 'Push notifications' })}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('config.subtitle', { defaultValue: 'Connect OneSignal to send push notifications to your web and mobile app.' })}</p>
        </div>
        <Link to="/notifications/send" className="text-sm font-medium text-brand underline-offset-4 hover:underline">
          {t('config.gotoSend', { defaultValue: 'Send a notification →' })}
        </Link>
      </header>

      <Card className="flex flex-col gap-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('config.title', { defaultValue: 'OneSignal' })}</h2>

        <label className="flex items-center gap-2 text-sm text-fg">
          <input type="checkbox" className="h-4 w-4 accent-brand" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          {t('config.enabled', { defaultValue: 'Enable push notifications' })}
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField id="pc-app" label={t('config.appId', { defaultValue: 'OneSignal App ID' })} helperText={t('config.appIdHint', { defaultValue: 'From OneSignal → Settings → Keys & IDs.' })}>
            <Input id="pc-app" className="font-mono" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" />
          </FormField>
          <FormField id="pc-key" label={t('config.apiKey', { defaultValue: 'REST API key' })} helperText={t('config.apiKeyHint', { defaultValue: 'Kept secret — used server-side to send.' })}>
            <div className="flex items-center gap-2">
              <Input
                id="pc-key" type="password" autoComplete="new-password" name="onesignal-rest-key"
                className="flex-1 font-mono" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                placeholder={config.restApiKeySet ? t('config.apiKeySet', { defaultValue: '•••••••• (set — type to replace)' }) : 'os_v2_app_…'}
              />
              {config.restApiKeySet && <Button type="button" variant="ghost" size="sm" onClick={clearKey} disabled={savingCfg}>{t('config.clearKey', { defaultValue: 'Remove' })}</Button>}
            </div>
          </FormField>
        </div>

        <FormField id="pc-seg" label={t('config.segment', { defaultValue: 'Default segment' })} helperText={t('config.segmentHint', { defaultValue: 'Who a MANUAL broadcast from the Send page reaches — every phone in this OneSignal segment. Nothing is sent automatically.' })}>
          <Input id="pc-seg" value={segment} onChange={(e) => setSegment(e.target.value)} />
        </FormField>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={saveConfig} loading={savingCfg}>{tc('actions.save')}</Button>
          <Button variant="secondary" onClick={runTest} loading={testing} disabled={!config.restApiKeySet}>{t('sendTest', { defaultValue: 'Send test' })}</Button>
        </div>
      </Card>
    </div>
  );
}
